import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { authFetch } from '../../api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiAlertCircle, FiCheckCircle } = FiIcons;

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [isValidToken, setIsValidToken] = useState(true);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      setStatus('error');
      setMessage('Reset token is missing from the URL.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing reset token.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const res = await authFetch('reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: password })
      });

      if (res?.status === 'success') {
        setStatus('success');
        setMessage('Your password has been successfully reset. You can now sign in.');
      } else {
        setStatus('error');
        setMessage(res?.error || 'Failed to reset password. The link may have expired.');
      }
    } catch (error) {
      console.error('Error in reset password:', error);
      setStatus('error');
      setMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="p-8">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiShield} className="text-[#00E5FF] text-xl" />
            </div>
            <span className="text-2xl font-bold text-slate-800 tracking-tight">Sentinel</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-2 text-center">Set New Password</h2>
          <p className="text-slate-500 mb-8 text-center text-sm">Please enter your new secure password below.</p>

          {status === 'error' && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{message}</p>
            </div>
          )}

          {status === 'success' ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheckCircle} className="text-3xl" />
              </div>
              <p className="text-sm text-slate-600 mb-6">{message}</p>
              <Link to="/login" className="inline-block bg-slate-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all">
                Go to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all text-sm"
                    placeholder="••••••••"
                    required
                    minLength={8}
                    disabled={!isValidToken || status === 'loading'}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'loading' || !isValidToken}
                className="w-full bg-slate-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 transition-all disabled:opacity-50 mt-6"
              >
                {status === 'loading' ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;